import { RedoSecondaryNavItem } from "@redotech/arbiter/navigation/secondary/redo-secondary-nav-item";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import REVIEWS_ICON from "@redotech/redo-icons/star-01.svg";
import REVIEWS_ICON_FILLED from "@redotech/redo-icons/star-01_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { useReviewsWorkspaceAccess } from "@redotech/redo-merchant-review-app/use-reviews-workspace-access";
import { memo, useContext } from "react";
import { useMatch } from "react-router-dom";
import { ReviewsToReviewContext } from "../../../../services/marketing/reviews-to-review-service";

export const ReviewsItems = memo(function ReviewsItems() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  const reviewCount = useContext(ReviewsToReviewContext);

  const { nativeReviewsEnabled, workspaceAvailable } =
    useReviewsWorkspaceAccess();

  const reviewsBaseUrl = routes.marketingBaseUrl + "/reviews";
  const anyChildSelected = useMatch(`${reviewsBaseUrl}/*`);

  if (!workspaceAvailable) {
    return null;
  }

  const defaultRoute = nativeReviewsEnabled ? "dashboard" : "all-reviews";

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getReviewsSubRoute(defaultRoute) }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={REVIEWS_ICON_FILLED}
      friendlyName={t("Reviews", {
        context: "merchant marketing navigation label",
      })}
      id="reviews"
      notificationCount={reviewCount}
      UnfilledIcon={REVIEWS_ICON}
    >
      {nativeReviewsEnabled && <ReviewsDashboardItem />}
      <ReviewsAllReviewsItem />
      <ReviewsProductsItem />
      {nativeReviewsEnabled && (
        <>
          <ReviewsDisplaysWidgetsItem />
          <ReviewsResponseItem />
          <ReviewsSettingsItem />
        </>
      )}
    </RedoSecondaryNavItem>
  );
});

const ReviewsDashboardItem = memo(function ReviewsDashboardItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getReviewsSubRoute("dashboard") }}
      friendlyName={t("Dashboard", {
        context: "merchant marketing navigation label",
      })}
      id="reviews-dashboard"
    />
  );
});

const ReviewsAllReviewsItem = memo(function ReviewsAllReviewsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getReviewsSubRoute("all-reviews") }}
      friendlyName={t("All reviews", {
        context: "merchant marketing navigation label",
      })}
      id="reviews-all-reviews"
    />
  );
});

const ReviewsDisplaysWidgetsItem = memo(function ReviewsDisplaysWidgetsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getReviewsSubRoute("reviews-displays-widgets"),
      }}
      friendlyName={t("Displays & widgets", {
        context: "merchant marketing navigation label",
      })}
      id="reviews-displays-widgets"
    />
  );
});

const ReviewsProductsItem = memo(function ReviewsProductsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getReviewsSubRoute("products") }}
      friendlyName={t("Products", {
        context: "merchant marketing navigation label",
      })}
      id="reviews-products"
    />
  );
});

const ReviewsResponseItem = memo(function ReviewsResponseItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getReviewsSubRoute("reviews-response"),
      }}
      friendlyName={t("Response", {
        context:
          "reviews navigation page for managing merchant review responses",
      })}
      id="reviews-response"
    />
  );
});

const ReviewsSettingsItem = memo(function ReviewsSettingsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getReviewsSubRoute("settings") }}
      friendlyName={t("Settings", {
        context: "merchant marketing navigation label",
      })}
      id="reviews-settings"
    />
  );
});
